import { useCallback, useEffect, useState } from 'react';
import { studentsAPI } from '../../services/api';
import { buildStudentChangeDisplayRows } from '../../utils/formatStudentChangeValue';

function formatWhen(iso) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return String(iso);
  }
}

/**
 * Recent audit trail for a student (admin + student self-edits).
 */
const StudentChangeHistory = ({ studentId, limit = 20, className = '' }) => {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [expandedId, setExpandedId] = useState(null);

  const load = useCallback(async () => {
    if (!studentId) return;
    setLoading(true);
    setError('');
    try {
      const res = await studentsAPI.getChangeLogs(studentId, { limit });
      setLogs(Array.isArray(res.data?.data) ? res.data.data : []);
    } catch (err) {
      setLogs([]);
      setError(err.response?.data?.message || err.message || 'Failed to load change history');
    } finally {
      setLoading(false);
    }
  }, [studentId, limit]);

  useEffect(() => {
    load();
  }, [load]);

  if (!studentId) return null;

  return (
    <div className={className}>
      <div className="flex items-center justify-between gap-2 mb-3">
        <h4 className="font-bold text-gray-800 text-sm uppercase tracking-wide border-b pb-2 flex-1">
          Change history
        </h4>
        <button
          type="button"
          onClick={load}
          className="text-xs underline text-gray-600 hover:text-gray-900"
          disabled={loading}
        >
          Refresh
        </button>
      </div>

      {loading ? <p className="text-sm text-gray-500">Loading history…</p> : null}
      {error ? <p className="text-sm text-red-600">{error}</p> : null}
      {!loading && !error && logs.length === 0 ? (
        <p className="text-sm text-gray-500">No recorded changes yet.</p>
      ) : null}

      <ul className="space-y-2">
        {logs.map((log) => {
          const open = expandedId === log.id;
          const changes = Array.isArray(log.changes) ? log.changes : [];
          return (
            <li key={log.id} className="rounded border border-gray-200 bg-white text-sm">
              <button
                type="button"
                className="w-full text-left px-3 py-2 flex flex-wrap items-center gap-x-3 gap-y-1"
                onClick={() => setExpandedId(open ? null : log.id)}
              >
                <span className="font-medium text-gray-800">{formatWhen(log.createdAt)}</span>
                <span
                  className="text-xs px-1.5 py-0.5 rounded uppercase tracking-wide"
                  style={{
                    backgroundColor: log.source === 'admin' ? '#dbeafe' : '#dcfce7',
                    color: log.source === 'admin' ? '#1e40af' : '#166534',
                  }}
                >
                  {log.source || 'system'}
                </span>
                <span className="text-gray-600 truncate">
                  {log.changedByEmail || log.changedByRole || 'Unknown'}
                </span>
                <span className="text-gray-500 text-xs truncate flex-1">
                  {log.summary || `${changes.length} field(s)`}
                </span>
                <span className="text-gray-400 text-xs">{open ? 'Hide' : 'Details'}</span>
              </button>
              {open ? (
                <div className="px-3 pb-3 border-t border-gray-100">
                  <table className="min-w-full text-xs mt-2">
                    <thead>
                      <tr className="text-left text-gray-500">
                        <th className="py-1 pr-3">Field</th>
                        <th className="py-1 pr-3">From</th>
                        <th className="py-1">To</th>
                      </tr>
                    </thead>
                    <tbody>
                      {changes.flatMap((c, idx) =>
                        buildStudentChangeDisplayRows(c).map((row, rowIdx) => (
                          <tr
                            key={`${log.id}-${c.field}-${idx}-${rowIdx}`}
                            className="align-top border-t border-gray-50"
                          >
                            <td className="py-1 pr-3 font-medium text-gray-800 whitespace-nowrap">
                              {row.fieldLabel}
                            </td>
                            <td className="py-1 pr-3 text-gray-600 max-w-xs">
                              <pre className="whitespace-pre-wrap font-sans text-xs m-0">
                                {row.fromText}
                              </pre>
                            </td>
                            <td className="py-1 text-gray-800 max-w-xs">
                              <pre className="whitespace-pre-wrap font-sans text-xs m-0">
                                {row.toText}
                              </pre>
                              {row.highlight ? (
                                <p className="mt-1 text-xs text-blue-800 bg-blue-50 border border-blue-100 rounded px-2 py-1 whitespace-pre-wrap">
                                  What changed: {row.highlight}
                                </p>
                              ) : null}
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              ) : null}
            </li>
          );
        })}
      </ul>
    </div>
  );
};

export default StudentChangeHistory;
